import { getAdvanceSummary } from '@op/components/GPT/utils';
import { DownloadConfigInfoType } from '../types';
import {
  dealwithDefaultGptText,
  dealwithGptText,
} from '@op/components/GPT/hooks/useMultipleGpt';
import {
  aigcDownloadAdvancedSummaryData,
  aigcGetSummary,
} from '@op/services/aigc';
import { objToUrl } from '@op/utils/url';
import { DBO_T } from '@op/utils/format';
import { languageRenderHelper } from '../renderHelper';
import { GptRequestType } from '@op/components/GPT/types';

export const GPTMultiple: DownloadConfigInfoType = {
  type: 'txt',
  customizeDownloadFunc: ({ auxiliaryParams }) => {
    const { GPTList, promptInfo, oriFilter } = auxiliaryParams;

    const promiseList = GPTList.map(({ filter, savePrefix }: any) => {
      return getAdvanceSummary({
        filter,
        promptInfo,
        oriFilter,
        savePrefix,
        stop: { current: null },
      }).then(dataInfo => {
        const { data, status, tips } = dataInfo || {};
        const { report_en } = (data as GptRequestType) || {};
        return {
          data: report_en,
          status,
          tips,
        };
      });
    });

    return Promise.all(promiseList).then(resultList => {
      const data = resultList.map((item, index) => {
        const otherInfo = GPTList[index];
        const { data, status } = item || {};

        return dealwithDefaultGptText(data, { ...otherInfo, status });
      });

      const value = dealwithGptText(data.flat(1), {});

      return {
        text: value.summary,
        status: value?.status || 'failed',
      };
    });
  },
};

export const GptCommonDownload: DownloadConfigInfoType = {
  type: 'txt',
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { promptInfo } = auxiliaryParams;
    const { data, status, tips } =
      (await getAdvanceSummary({ filter, promptInfo })) || {};
    const { report_en } = data as GptRequestType;
    let text = report_en;
    return {
      text: DBO_T.mdToText(text),
      status: status || 'failed',
      tips,
    };
  },
};

function getDatabrainUrlRender(gameid: string) {
  return (item: any, key: string) => {
    const data = {
      gameid,
      comment_time: item.comment_time,
    };
    const paramsUrl = objToUrl(data);
    return `${location.origin}/opinion/Feeds/Feeds/Details/${item.comment_uin}?${paramsUrl}`;
  };
}

export const GptAdvanceDownload: DownloadConfigInfoType = {
  type: 'zip',
  files: [
    {
      type: 'txt',
      key: 'summary_cn',
      fileName: 'Advanced AI Report_Chinese',
    },
    {
      type: 'txt',
      key: 'summary_en',
      fileName: 'Advanced AI Report_English',
    },
    {
      type: 'xlsx',
      key: 'advance_excel',
      fileName: 'Advanced AI Report Reviews_English',
      sheetNames: [
        { name: 'POS', key: 'positive', addOn: '', replaceName: '' },
        { name: 'NEG', key: 'negative', addOn: '', replaceName: '' },
        { name: 'NEUTRAL', key: 'neutral', addOn: '', replaceName: '' },
      ],
      columns: ({ filter }) => {
        return [
          { key: 'opinion', header: 'opinion' },
          { key: 'overview', header: 'overview' },
          { key: 'content', header: 'content' },
          { key: 'content_to_en', header: 'content_to_en' },
          { key: 'sentiment_rating', header: 'sentiment_rating' },
          { key: 'report_time', header: 'report_time' },
          { key: 'comment_time', header: 'comment_time' },
          { key: 'topics', header: 'topics' },
          { key: 'channel_name', header: 'channel_name' },
          {
            key: 'language',
            header: 'language',
            renderHelper: [languageRenderHelper],
          },
          { key: 'content_url', header: 'content_url' },
          {
            key: 'databrain_url',
            header: 'databrain_url',
            renderHelper: [getDatabrainUrlRender(filter.edition_unified_id)],
          },
          { key: 'engagement', header: 'engagement' },
        ];
      },
    },
    {
      type: 'xlsx',
      key: 'advance_excel',
      fileName: 'Advanced AI Report Reviews_Chinese',
      sheetNames: [
        { name: 'POS', key: 'positive', addOn: '', replaceName: '' },
        { name: 'NEG', key: 'negative', addOn: '', replaceName: '' },
        { name: 'NEUTRAL', key: 'neutral', addOn: '', replaceName: '' },
      ],
      columns: ({ filter }) => {
        return [
          { key: 'opinion_zh', header: 'opinion' },
          { key: 'overview_zh', header: 'overview' },
          { key: 'content', header: 'content' },
          { key: 'content_to_zh', header: 'content_to_zh' },
          { key: 'sentiment_rating', header: 'sentiment_rating' },
          { key: 'report_time', header: 'report_time' },
          { key: 'comment_time', header: 'comment_time' },
          { key: 'topics', header: 'topics' },
          { key: 'channel_name', header: 'channel_name' },
          {
            key: 'language',
            header: 'language',
            renderHelper: [languageRenderHelper],
          },
          { key: 'content_url', header: 'content_url' },
          {
            key: 'databrain_url',
            header: 'databrain_url',
            renderHelper: [getDatabrainUrlRender(filter.edition_unified_id)],
          },
          { key: 'engagement', header: 'engagement' },
        ];
      },
    },
  ],
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { promptInfo, advanceFilter } = auxiliaryParams;
    try {
      const { data, status, tips } =
        (await getAdvanceSummary({ filter: filter, promptInfo })) || {};
      const excelData = await aigcDownloadAdvancedSummaryData(advanceFilter);
      const { report_en, report_zh } = data as GptRequestType;

      return {
        summary_cn: DBO_T.mdToText(report_zh),
        summary_en: DBO_T.mdToText(report_en),
        advance_excel: excelData,
        status: status || 'failed',
        tips,
      };
    } catch (error) {
      return {
        tips: {
          summary_cn: 'failed',
          summary_en: 'failed',
          advance_excel: 'failed',
        },
      };
    }
  },
};

export const GptAdvancedDataDownload: DownloadConfigInfoType = {
  type: 'zip',
  files: [
    {
      type: 'xlsx',
      key: 'advance_excel',
      fileName: 'Advanced AI Report Reviews_English',
      sheetNames: [
        { name: 'POS', key: 'positive', addOn: '', replaceName: '' },
        { name: 'NEG', key: 'negative', addOn: '', replaceName: '' },
        { name: 'NEUTRAL', key: 'neutral', addOn: '', replaceName: '' },
      ],
      columns: ({ filter }) => {
        return [
          { key: 'opinion', header: 'opinion' },
          { key: 'overview', header: 'overview' },
          { key: 'content', header: 'content' },
          { key: 'content_to_en', header: 'content_to_en' },
          { key: 'sentiment_rating', header: 'sentiment_rating' },
          { key: 'report_time', header: 'report_time' },
          { key: 'comment_time', header: 'comment_time' },
          { key: 'topics', header: 'topics' },
          { key: 'channel_name', header: 'channel_name' },
          {
            key: 'language',
            header: 'language',
            renderHelper: [languageRenderHelper],
          },
          { key: 'content_url', header: 'content_url' },
          {
            key: 'databrain_url',
            header: 'databrain_url',
            renderHelper: [getDatabrainUrlRender(filter.edition_unified_id)],
          },
          { key: 'engagement', header: 'engagement' },
        ];
      },
    },
    {
      type: 'xlsx',
      key: 'advance_excel',
      fileName: 'Advanced AI Report Reviews_Chinese',
      sheetNames: [
        { name: 'POS', key: 'positive', addOn: '', replaceName: '' },
        { name: 'NEG', key: 'negative', addOn: '', replaceName: '' },
        { name: 'NEUTRAL', key: 'neutral', addOn: '', replaceName: '' },
      ],
      columns: ({ filter }) => {
        return [
          { key: 'opinion_zh', header: 'opinion' },
          { key: 'overview_zh', header: 'overview' },
          { key: 'content', header: 'content' },
          { key: 'content_to_zh', header: 'content_to_zh' },
          { key: 'sentiment_rating', header: 'sentiment_rating' },
          { key: 'report_time', header: 'report_time' },
          { key: 'comment_time', header: 'comment_time' },
          { key: 'topics', header: 'topics' },
          { key: 'channel_name', header: 'channel_name' },
          {
            key: 'language',
            header: 'language',
            renderHelper: [languageRenderHelper],
          },
          { key: 'content_url', header: 'content_url' },
          {
            key: 'databrain_url',
            header: 'databrain_url',
            renderHelper: [getDatabrainUrlRender(filter.edition_unified_id)],
          },
          { key: 'engagement', header: 'engagement' },
        ];
      },
    },
  ],
  customizeDownloadFunc: async ({ filter }) => {
    try {
      const excelData = await aigcDownloadAdvancedSummaryData(filter);
      return {
        advance_excel: excelData,
        status: 'success',
      };
    } catch (error) {
      return {
        tips: {
          advance_excel: 'failed',
        },
      };
    }
  },
};

export const GptFileZipDownload: DownloadConfigInfoType = {
  type: 'fileZip',
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { task_id } = auxiliaryParams || {};
    const summaryData = await aigcGetSummary({
      game_id: filter?.edition_unified_id,
      task_id,
    });

    const files = summaryData?.files || {};
    const downloadUrls: Array<{ url: string; fileName: string }> = [];
    if (files.summary_excel_url_en) {
      downloadUrls.push({
        url: files.summary_excel_url_en,
        fileName: 'Advanced AI Report Reviews_English.xlsx',
      });
    }
    if (files.summary_excel_url_zh) {
      downloadUrls.push({
        url: files.summary_excel_url_zh,
        fileName: 'Advanced AI Report Reviews_Chinese.xlsx',
      });
    }
    if (files.summary_md_url_en) {
      downloadUrls.push({
        url: files.summary_md_url_en,
        fileName: 'Advanced AI Report_English.md',
      });
    }
    if (files.summary_md_url_zh) {
      downloadUrls.push({
        url: files.summary_md_url_zh,
        fileName: 'Advanced AI Report_Chinese.md',
      });
    }

    const fileData: Array<{ fileName: string; data: Blob }> = [];
    for (const item of downloadUrls) {
      if (item.url) {
        const res = await fetch(item.url);
        fileData.push({ fileName: item.fileName, data: await res.blob() });
      }
    }

    if (fileData.length === 0) {
      return { status: 'failed', tips: 'file_failed' };
    }

    return { fileData, status: 'success' };
  },
};
